import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import { i18n } from "./i18n";
import "./assets/main.css";
// AG Grid base CSS + Alpine theme (required for the grid to render)
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

/**
 * Bootstrap the app only after the persisted session is restored.
 *
 * Why: Vue Router fires beforeEach guards synchronously as part of the initial
 * navigation, BEFORE onMounted() in App.vue ever runs.  If we call
 * tryRestoreSession() only in onMounted we get a race: guard sees
 * isAuthenticated=false (user is null) and redirects to /auth/login on every
 * hard refresh — even when a valid access_token is in localStorage.
 *
 * Awaiting tryRestoreSession() here guarantees user.value is populated
 * before the first navigation guard runs.
 */
async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(router);
  app.use(i18n);

  // global directive to translate any plain English text nodes based on current locale
  app.directive("locale", {
    mounted(el) {
      const translate = () => {
        function walk(node: Node) {
          node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              const txt = child.textContent?.trim();
              // skip if text is only punctuation/quotes/numbers
              if (txt && /[\p{Letter}]/u.test(txt)) {
                child.textContent = i18n.global.t(txt as string);
              }
            } else {
              walk(child);
            }
          });
        }
        walk(el);
      };
      translate();
      // watch locale changes
      import("vue").then(({ watch }) => {
        watch(() => i18n.global.locale.value, translate);
      });
    },
    updated(el) {
      const translate = () => {
        function walk(node: Node) {
          node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              const txt = child.textContent?.trim();
              if (txt && /[\p{Letter}]/u.test(txt)) {
                child.textContent = i18n.global.t(txt as string);
              }
            } else {
              walk(child);
            }
          });
        }
        walk(el);
      };
      translate();
    },
  });

  // Register global error handler plugin (401→login, 403→unauthorized, 500→log)
  const { errorHandlerPlugin } = await import("@/plugins/errorHandler");
  app.use(errorHandlerPlugin, { router });

  // Start client observability logger (batched, periodic flush)
  const { logger } = await import("@/services/logger");
  logger.start();

  // Register custom tooltip directive globally (v-tooltip="'text'")
  // and restore the saved tooltip style preference on <body>
  const { vTooltip, initTooltipStyle } = await import("@/directives/tooltip");
  app.directive("tooltip", vTooltip);
  initTooltipStyle();

  const { useAuthStore } = await import("@/stores/auth");
  const auth = useAuthStore();
  await auth.tryRestoreSession();

  // Hydrate feature flags ONLY after confirming the user session is valid.
  // Calling load() before auth resolves causes a 401 on the login page.
  if (auth.isAuthenticated) {
    import("@/stores/features")
      .then(({ useFeatureStore }) => useFeatureStore().load())
      .catch(() => {});
  }

  app.mount("#app");
}

bootstrap();
